import * as React from 'react';

import { translate } from '~l10n/client';
import { useAtom } from 'jotai';
import { RESET, atomWithReset } from 'jotai/utils';
import { SubmitHandler, useForm } from 'react-hook-form';

import { AlertMessage } from '~shared/components/AlertMessage';
import { SmallButton } from '~shared/components/buttons';
import { Dialog } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import TextInput from '~shared/components/TextInput';
import { useSocrativeRouter } from '~teacher/context/RouterContext';
import { useAccount, useUpdateEmail } from '~teacher/hook/account';

import {
  MAX_EMAIL_LENGTH,
  validEmailRegEx,
} from '../../../../../../../shared/isValidEmail';
import { isAxiosError } from '../../../../api/isAxiosError';

const updateEmailDialogAtom = atomWithReset<boolean>(false);
updateEmailDialogAtom.onMount = (set) => {
  return () => {
    set(RESET);
  };
};

type UpdateEmailFormSchema = {
  newEmail: string;
  confirmEmail: string;
  password: string;
};

export function UpdateEmailDialog() {
  const updateEmailMutation = useUpdateEmail();
  const currentEmailQuery = useAccount({ select: ({ email }) => email });
  const [isOpen, setIsOpen] = useAtom(updateEmailDialogAtom);
  const teacherRouter = useSocrativeRouter();

  const { register, handleSubmit, formState, reset, setError } =
    useForm<UpdateEmailFormSchema>({
      mode: 'onSubmit',
      reValidateMode: 'onSubmit',
      defaultValues: {
        newEmail: '',
        confirmEmail: '',
        password: '',
      },
    });

  const onClose = () => {
    setIsOpen(RESET);
    reset();
  };

  const onSubmit: SubmitHandler<UpdateEmailFormSchema> = ({
    password,
    newEmail,
  }) => {
    updateEmailMutation.mutate(
      {
        password,
        newEmail,
      },
      {
        onSuccess: () => {
          onClose();
          teacherRouter.navigate('#email-verify', {
            trigger: true,
            replace: true,
          });
        },
        onError: (error: unknown) => {
          if (isAxiosError(error)) {
            const errorCode = error.response?.data?.code;
            switch (errorCode) {
              case 'same_email':
                setError('newEmail', {
                  type: 'manual',
                  message: translate('Please enter a new email.'),
                });
                break;
              case 'invalid_password':
              case 'email_already_in_use':
                setError('newEmail', {
                  type: 'manual',
                  message: translate(
                    'Invalid password or new email is already in use.'
                  ),
                });
                break;
              default:
                // TODO: handle different server errors.
                setError('root', {
                  type: 'manual',
                  message: translate(
                    'An error occurred. Please check your connection and try again.'
                  ),
                });
                break;
            }
          } else {
            // TODO: handle different server errors.
            setError('root', {
              type: 'manual',
              message: translate(
                'An error occurred. Please check your connection and try again.'
              ),
            });
          }
        },
      }
    );
  };

  const firstError = ['newEmail', 'confirmEmail', 'password', 'root'].find(
    (field) => formState.errors[field]
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      id={'update-email-dialog'}
      title="Update Email"
    >
      <form
        method="dialog"
        className="flex flex-col pt-5 gap-8"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-5">
          <div>
            <Divider />
            <div className="py-5">
              <p className="font-sans font-400 text-base leading-normal">
                {translate(
                  'After changing your email you will need to verify your email before the update can complete.'
                )}
              </p>
            </div>
            <Divider />
          </div>

          <label className="flex flex-col gap-3 font-sans font-400 text-base leading-normal">
            {translate('New Email')}
            <TextInput
              required
              aria-invalid={formState.errors.newEmail ? 'true' : 'false'}
              id="new-email"
              type="email"
              {...register('newEmail', {
                validate: {
                  new_email: (newEmail: string) => {
                    if (newEmail === currentEmailQuery.data) {
                      return translate('Please enter a new email.');
                    }
                  },
                },
                onChange: (e) => {
                  e.target.value = e.target.value.toLowerCase();
                },
                required: {
                  value: true,
                  message: translate('Please enter a valid email'),
                },
                pattern: {
                  value: validEmailRegEx,
                  message: translate('Please enter a valid email'),
                },
                maxLength: {
                  value: MAX_EMAIL_LENGTH,
                  message: translate('Please enter a valid email'),
                },
              })}
            />
          </label>

          <label
            className="flex flex-col gap-3  font-sans font-400 text-base leading-normal"
            htmlFor="confirm-new-email"
          >
            {translate('Confirm Email')}
            <TextInput
              name="confirm-new-email"
              id="confirm-new-email"
              aria-invalid={formState.errors.confirmEmail ? 'true' : 'false'}
              type="email"
              {...register('confirmEmail', {
                validate: {
                  emailMatch: (confirmEmail, formData) => {
                    if (confirmEmail !== formData.newEmail)
                      return translate('Please ensure that emails match.');
                  },
                },
                onChange: (e) => {
                  e.target.value = e.target.value.toLowerCase();
                },
              })}
            />
          </label>

          <label
            className="flex flex-col gap-3 font-sans font-400 text-base leading-normal"
            htmlFor="current-password"
          >
            {translate('Your password is required to complete this process:')}
            <TextInput
              type="password"
              id="current-password"
              {...register('password', {
                required: {
                  value: true,
                  message: translate('Check password and try again.'),
                },
              })}
            />
          </label>
        </div>

        <div className="flex flex-col gap-2" aria-live="polite">
          {firstError ? (
            <AlertMessage tone="critical" showIcon>
              {formState.errors[firstError].message}
            </AlertMessage>
          ) : null}
        </div>

        <div className="flex flex-row-reverse gap-4">
          <SmallButton
            appearance="primary"
            type="submit"
            isLoading={updateEmailMutation.isLoading}
          >
            {translate('Verify Email')}
          </SmallButton>
          <SmallButton onClick={onClose} type="reset">
            {translate('Cancel')}
          </SmallButton>
        </div>
      </form>
    </Dialog>
  );
}

UpdateEmailDialog.useIsOpen = () => useAtom(updateEmailDialogAtom);
